import { lazy } from "react";
import { useZaehlerOffen, useZaehlerSprint, useZaehlerWoche } from "./zaehler";
import { NEUE_AUFGABE } from "../../kern/dialog";
import type { Modul } from "../../kern/modul";
import { Alle, Offen } from "./Alle";
import { AufgabeDialog, AufgabeWeiterleitung, NeuWeiterleitung } from "./Dialog";
import { ArchivSeite, BacklogSeite, SprintSeite } from "./Sprint";
import { Woche } from "./Woche";
import { useMitteilungOrte } from "./mitteilungOrte";
import { useInEinemVorhaben, useVorhabenInNavigation, useVorhabenUnterpunkte } from "./vorhaben";
import { LineupZahl } from "./LineupZahl";

// Vorhaben erst laden, wenn jemand die Seiten öffnet (BAUPLAN-PAKETE.md, Abschnitt 5.7)
const VorhabenSeite = lazy(() => import("./VorhabenUebersicht").then((m) => ({ default: m.VorhabenSeite })));
const KapselSeite = lazy(() => import("./Vorhaben").then((m) => ({ default: m.KapselSeite })));
// Vorschau einer Unterlage für andere Module (Budget), erst beim Öffnen geladen (Paket E2)
const UnterlageVorschau = lazy(() => import("./UnterlageVorschau"));
// Reiter „Line-up“ der Kapsel über das Kern-Register wie das Budget (Paket E3), erst beim Öffnen geladen
const KapselLineup = lazy(() => import("./Lineup"));

export const aufgaben: Modul = {
  id: "aufgaben",
  name: "Aufgaben",
  routen: [
    { pfad: "/", seite: Woche },
    { pfad: "/sprint", seite: SprintSeite, recht: "sprint.planen" },
    { pfad: "/sprint/backlog", seite: BacklogSeite, recht: "sprint.planen" },
    { pfad: "/sprint/archiv", seite: ArchivSeite, recht: "sprint.planen" },
    { pfad: "/aufgaben", seite: Alle },
    { pfad: "/offen", seite: Offen },
    { pfad: "/neu", seite: NeuWeiterleitung },
    { pfad: "/aufgaben/:id", seite: AufgabeWeiterleitung },
    // Ohne Recht: Mitglieder sehen ihre Vorhaben, RLS entscheidet (Paket C1)
    { pfad: "/vorhaben", seite: VorhabenSeite },
    { pfad: "/vorhaben/:id", seite: KapselSeite },
  ],
  navigation: [
    { bereich: "start", label: "Meine Woche", pfad: "/", icon: "woche", leiste: 1, leisteLabel: "Woche", reihenfolge: 0, zaehler: useZaehlerWoche },
    // Untere Leiste: Sprint verdrängt Offen, wenn die Person den Sprint planen darf (BAUPLAN-PAKETE.md, Abschnitt 4.2)
    { bereich: "aufgaben", label: "Sprint", pfad: "/sprint", icon: "sprint", recht: "sprint.planen", leiste: 2, leisteRang: 0, reihenfolge: 5, zaehler: useZaehlerSprint },
    // Ein Eintrag „Aufgaben“ mit Reitern Alle und Offen, Zähler von Offen (BAUPLAN-SEITENLEISTE.md, Abschnitt 4). Die Pfade bleiben.
    { bereich: "aufgaben", gruppe: "Aufgaben", label: "Alle", pfad: "/aufgaben", icon: "liste", reihenfolge: 10 },
    { bereich: "aufgaben", gruppe: "Aufgaben", label: "Offen", pfad: "/offen", icon: "offen", leiste: 2, reihenfolge: 20, zaehler: useZaehlerOffen },
    // Vorhaben (BAUPLAN-PAKETE.md, Abschnitt 9.1): Vorstand immer, Mitglieder nur, wenn sie in einem sind. Darunter die eigenen aktiven
    // Vorhaben (BAUPLAN-SEITENLEISTE.md, Abschnitt 7, nimmt die Regel vom 15.10. zurück).
    // Untere Leiste: vor Offen, wenn die Person in einem Vorhaben ist. Sprint (Rang 0, ohne Bedingung) geht vor.
    {
      bereich: "aufgaben",
      label: "Vorhaben",
      pfad: "/vorhaben",
      icon: "vorhaben",
      reihenfolge: 30,
      leiste: 2,
      leisteRang: 10,
      bedingung: useVorhabenInNavigation,
      leisteBedingung: useInEinemVorhaben,
      unterpunkte: useVorhabenUnterpunkte,
    },
    // Nur in der unteren Leiste am Handy. Öffnet den Dialog über der aktuellen Seite, wie der Knopf „Neue Aufgabe“ auf jeder Seite.
    { bereich: "aufgaben", label: "Neue Aufgabe", pfad: "/neu", dialog: NEUE_AUFGABE, icon: "plus", leiste: 3, leisteLabel: "Neu", reihenfolge: 90 },
  ],
  ereignisse: [
    "aufgaben.angelegt",
    "aufgaben.bearbeitet",
    "aufgaben.zugewiesen",
    "aufgaben.uebernommen",
    "aufgaben.abgegeben",
    "aufgaben.status_geaendert",
    "aufgaben.erledigt",
    "aufgaben.archiviert",
    "aufgaben.wiederhergestellt",
    "aufgaben.importiert",
    "aufgaben.import_zugewiesen",
    "aufgaben.in_sprint",
    "aufgaben.in_backlog",
    "aufgaben.wieder_geoeffnet",
    "aufgaben.sprint_bearbeitet",
    "aufgaben.sprint_abgeschlossen",
    "aufgaben.person_hinzugefuegt",
    "aufgaben.person_entfernt",
    "aufgaben.kommentiert",
    "aufgaben.kommentar_entfernt",
    "aufgaben.vorhaben_angelegt",
    "aufgaben.vorhaben_bearbeitet",
    "aufgaben.vorhaben_archiviert",
    "aufgaben.vorhaben_person_gesetzt",
    "aufgaben.vorhaben_person_entfernt",
    "aufgaben.vorhaben_summen_gesetzt",
  ],
  ueberlagerungen: [AufgabeDialog],
  mitteilungOrte: useMitteilungOrte,
  unterlageVorschau: UnterlageVorschau,
  // Alle Personen der Kapsel sehen das Line-up (ohne recht und pruefen), zwischen Budget (20) und Unterlagen (30). Nur bei Veranstaltungen.
  kapselReiter: [{ id: "lineup", label: "Line-up", arten: ["veranstaltung"], reihenfolge: 25, zahl: LineupZahl, inhalt: KapselLineup }],
};
